import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Util, UUID} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {definitions} from '@api/definitions'
import {PermissionsChecker} from '@client/permissions-checker'
import {MonacoEditor} from '@common/monaco-editor'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {Company} from '@extensions/company'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {ScriptsApiNavStore} from './scripts-api-nav-store'
import {ScriptsCodeStore} from './scripts-code-store'
import {ScriptsObjNavStore} from './scripts-obj-nav-store'

type OpenedNav = 'obj' | 'api' | 'help'

interface State {
  companyName?: string
  openedNav: OpenedNav
  fullscreenAllowed: boolean
  scriptsOpenInterval?: NodeJS.Timeout
  updateInterval?: NodeJS.Timeout
  scriptsOpenSaved: boolean
  companyChangeListener?: number
}

export interface ScriptsGlobalState {
  monaco?: MonacoEditor
  monacoTabId?: UUID
  companyId: DataObjectId
  codeStore: ScriptsCodeStore
  objNavStore: ScriptsObjNavStore
  apiNavStore: ScriptsApiNavStore
  monacoContainer?: HTMLElement
}

export class ScriptsStore {
  globalState: ScriptsGlobalState
  state: State

  constructor() {
    const company = Company.getCurrent()
    this.state = {
      companyName: company.name,
      openedNav: 'api',
      fullscreenAllowed: !Util.isDesktop(),
      scriptsOpenSaved: globals.scriptsOpen
    }

    this.globalState = {
      codeStore: new ScriptsCodeStore(this),
      objNavStore: new ScriptsObjNavStore(this),
      apiNavStore: new ScriptsApiNavStore(this),
      companyId: company.id
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        globalState: false
      },
      {autoBind: true}
    )
  }

  init() {
    this.state.companyChangeListener = globals.onCompanyChange(() => {
      this.close()
      const company = Company.getCurrent()
      this.state.companyName = company.name
      this.globalState.companyId = company.id
    })

    window.addEventListener('keydown', this.onKeyDown)
    this.state.scriptsOpenInterval = setInterval(this.syncOpenClose, 500)
    this.scheduledUpdate()
  }

  syncOpenClose(): void {
    if (globals.scriptsOpen !== this.state.scriptsOpenSaved) {
      this.state.scriptsOpenSaved = globals.scriptsOpen
      globals.scriptsOpenUpdated?.()
      globals.scriptsOpen && void Tracking.send(TrackingEvent.scriptsOpen)
      globals.scriptsOpen && this.initEditor()
    }
  }

  close(): void {
    globals.scriptsOpen = false
    this.syncOpenClose()
  }

  initEditor(el?: HTMLDivElement): void {
    this.globalState.monacoContainer ??= el
    if (!this.globalState.monacoContainer || !globals.scriptsOpen) {
      return
    }

    MonacoEditor.init(definitions, () => {
      this.globalState.monaco = new MonacoEditor(
        this.globalState.monacoContainer
      )
      const code = this.globalState.codeStore.state
      if (code.openedTab) {
        this.globalState.monacoTabId = code.openedTab.id
        this.globalState.monaco.setValue(code.codes[code.openedTab.id])
        this.globalState.monaco.open()
      }
    })
  }

  scheduledUpdate(): void {
    const checkUpdated = (): void => {
      const {openedTab, codes} = this.globalState.codeStore.state
      if (!globals.scriptsOpen || !openedTab || !this.globalState.monaco) {
        return
      }
      if (this.globalState.monacoTabId !== openedTab.id) {
        return
      }

      if (codes[openedTab.id] !== this.globalState.monaco.getValue()) {
        if (openedTab instanceof Hub) {
          const oldCode = codes[openedTab.id]
          const newCode = this.globalState.monaco.getValue()
          if (oldCode !== newCode) {
            codes[openedTab.id] = newCode
          }
        } else {
          MessageBox.warn(Lang.msg.readonlyScriptWarn)
          this.globalState.monaco.setValue(codes[openedTab.id])
        }
      }
    }

    this.state.updateInterval = setInterval(() => {
      try {
        checkUpdated()
      } catch (e) {
        logger.error(e)
      }
    }, 500)
  }

  dispose(): void {
    globals.removeCompanyListener(this.state.companyChangeListener)
    window.removeEventListener('keydown', this.onKeyDown)
    clearInterval(this.state.scriptsOpenInterval)
    clearInterval(this.state.updateInterval)
    this.globalState.codeStore.dispose()
  }

  isFullscreen(): boolean {
    return PageService.getPage() === 'api-editor'
  }

  goToFeedback(): void {
    PageService.goTo('feedback')
  }

  insertIntoEditor(code: string): void {
    if (this.globalState.codeStore.state.openedTab && this.globalState.monaco) {
      this.globalState.monaco.insertAtCursor(code)
    } else {
      MessageBox.warn(Lang.msg.openEditorWarn)
    }
  }

  setOpenedNav(nav: OpenedNav): void {
    this.state.openedNav = nav
  }

  onKeyDown(event: KeyboardEvent): void {
    if ((event.keyCode === 192 || event.keyCode === 221) && event.ctrlKey) {
      // ctrl + ` or ctrl + ]
      if (PermissionsChecker.isAdmin()) {
        globals.scriptsOpen = !globals.scriptsOpen
        this.syncOpenClose()
      }
    }
    const code = this.globalState.codeStore.state
    if (globals.scriptsOpen && code.openedTab) {
      if (event.keyCode === 83 && (event.ctrlKey || event.metaKey)) {
        // cmd + S
        event.preventDefault()
        if (code.openedTab instanceof Hub) {
          this.globalState.codeStore.saveScript()
        } else {
          MessageBox.warn(Lang.msg.readonlyScriptWarn)
        }
      }
    }
  }
}

export const [ScriptsContext, useScriptsStore] = createUseStore(ScriptsStore)
